import { ColDef, ICellEditorParams, ValueFormatterParams } from "ag-grid-community";
import moment from "moment";
import ProgressIndicatorModel from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import { TrainingModuleField, defaultDateTimeString } from "../../../../../../enums";
import TrainingModulesApi from "../../../../../../services/api/v2/trainingModules/TrainingModules.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import I18n from "../../../../../localization/I18n";
import { laterDateComparator } from "../../../../GridHelpers";
import {
  CommonColDefFieldNamesEnum,
  TrainingModuleColDefFieldNamesEnum
} from "../../../enums/AgGridColDefFieldNameEnum";
import { BASE_FILTER_CONFIG } from "../../columns/baseColumn/BaseColumn_config";
import { AutocompleteColumnBuilder } from "../../columns/commonColumns/AutocompleteColumn/AutocompleteColumn_builder";
import {
  AUTOCOMPLETE_COLUMN_CONFIG,
  AUTOCOMPLETE_FILTER_CONFIG
} from "../../columns/commonColumns/AutocompleteColumn/AutocompleteColumn_config";
import {
  OWNER_NAME_COLUMN_CONFIG,
  OWNER_NAME_FILTER_CONFIG
} from "../../columns/commonColumns/AutocompleteColumn/ownerName/OwnerNameColumn_config";
import { DateColumnBuilder } from "../../columns/commonColumns/DateColumn/DateColumn_builder";
import { DATE_COLUMN_CONFIG, DATE_FILTER_CONFIG } from "../../columns/commonColumns/DateColumn/DateColumn_config";
import { DescriptionColumnBuilder } from "../../columns/commonColumns/DescriptionColumn/DescriptionColumn_builder";
import {
  DESCRIPTION_COLUMN_CONFIG,
  DESCRIPTION_FILTER_CONFIG
} from "../../columns/commonColumns/DescriptionColumn/DescriptionColumn_config";
import { DurationColumnBuilder } from "../../columns/commonColumns/DurationColumn/DurationColumn_builder";
import {
  DURATION_COLUMN_CONFIG,
  DURATION_FILTER_CONFIG
} from "../../columns/commonColumns/DurationColumn/DurationColumn_config";
import { NameColumnBuilder } from "../../columns/commonColumns/NameColumn/NameColumn_builder";
import { NAME_COLUMN_CONFIG, NAME_FILTER_CONFIG } from "../../columns/commonColumns/NameColumn/NameColumn_config";
import { SelectionColumnBuilder } from "../../columns/commonColumns/SelectionColumn/SelectionColumn_builder";
import { SimpleTextColumnBuilder } from "../../columns/commonColumns/SimpleTextColumn/SimpleTextColumn_builder";
import { SIMPLE_TEXT_COLUMN_CONFIG } from "../../columns/commonColumns/SimpleTextColumn/SimpleTextColumn_config";
import { BaseGridColumnBuilder } from "../base/BaseGridColumnBuilder";
import { TRAINING_MODULE_ACTIONS_SIDEBAR_MODAL_CONFIG } from "./modals/ActionLinkModal/ActionLinkModal_config";
import { TRAINING_MODULE_PROCESSES_SIDEBAR_MODAL_CONFIG } from "./modals/ProcessLinkModal/ProcessLinkModal_config";

export interface TrainingModulesGridColumnBuilderProps {
  canEdit: boolean;
  organisationId: number;
  projectId: number;
  userCanViewTrainingModules: boolean;
  projectTeamMembers: any[];
  columns: string[];
  onFieldUpdate: () => void;
  trainingProgress: any[];
}

export class TrainingModulesGridColumnBuilder extends BaseGridColumnBuilder {
  gridColumns: Dictionary<ColDef>;
  gridToastService = GridToastService;
  httpProgress = ProgressIndicatorModel;
  gridProps: TrainingModulesGridColumnBuilderProps;
  columnDefs: Dictionary<() => ColDef>;
  organisationId: number;
  projectTeamMembers: any[];
  projectId: number;
  onFieldUpdate: () => void;
  trainingProgress: any[];

  constructor(gridProps: TrainingModulesGridColumnBuilderProps) {
    super(TrainingModulesApi.updateField, gridProps.organisationId, gridProps.projectId, gridProps.canEdit);
    this.gridProps = gridProps;
    this.organisationId = gridProps.organisationId;
    this.projectId = gridProps.projectId;
    this.projectTeamMembers = gridProps.projectTeamMembers;
    this.onFieldUpdate = gridProps.onFieldUpdate;
    this.trainingProgress = gridProps.trainingProgress;
    this.init();
  }

  private init = () => {
    this.columnDefs = {
      [CommonColDefFieldNamesEnum.Selected]: () =>
        new SelectionColumnBuilder().makeSelectable().generateColumnOptions(),
      [CommonColDefFieldNamesEnum.Name]: () => this.buildNameColumn(),
      [TrainingModuleColDefFieldNamesEnum.ModuleId]: () => this.buildModuleIdColumn(),
      [CommonColDefFieldNamesEnum.Description]: () => this.buildDescriptionColumn(),
      [TrainingModuleColDefFieldNamesEnum.Duration]: () => this.buildDurationColumn(),
      [CommonColDefFieldNamesEnum.StartDate]: () => this.buildStartDateColumn(),
      [CommonColDefFieldNamesEnum.EndDate]: () => this.buildEndDateColumn(),
      [CommonColDefFieldNamesEnum.Owner]: () => this.buildOwnerColumn(),
      [TrainingModuleColDefFieldNamesEnum.TrainingProgress]: () => this.buildTrainingProgressColumn(),
      [TrainingModuleColDefFieldNamesEnum.TrainingProgressPercentage]: () =>
        this.buildTrainingProgressPercentageColumn(),
      [CommonColDefFieldNamesEnum.Actions]: () =>
        this.buildPillsColumn(
          CommonColDefFieldNamesEnum.Actions,
          I18n.t("grids.actions"),
          TRAINING_MODULE_ACTIONS_SIDEBAR_MODAL_CONFIG,
          null,
          this.onFieldUpdate
        )
          .makeEditable(false)
          .makeReadOnly(false)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.Processes]: () =>
        this.buildPillsColumn(
          CommonColDefFieldNamesEnum.Processes,
          I18n.t("grids.processesFromTrainingModule"),
          TRAINING_MODULE_PROCESSES_SIDEBAR_MODAL_CONFIG,
          null,
          this.onFieldUpdate
        ).generateColumnOptions(),
      [CommonColDefFieldNamesEnum.CreatedBy]: () =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.CreatedBy,
          headerName: I18n.t("grids.createdBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.CreatedAt]: () =>
        new DateColumnBuilder({ field: CommonColDefFieldNamesEnum.CreatedAt, headerName: I18n.t("grids.createdOn") })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.CreatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.CreatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.ModifiedBy]: () =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.ModifiedBy,
          headerName: I18n.t("grids.lastModifiedBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .setFilterOptions(NAME_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.UpdatedAt]: () =>
        new DateColumnBuilder({
          field: CommonColDefFieldNamesEnum.UpdatedAt,
          headerName: I18n.t("grids.lastModifiedOn")
        })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.UpdatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.UpdatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions()
    };
  };

  generateColumnDefs = (): ColDef[] => {
    let res: ColDef[] = [];
    this.gridProps.columns.forEach(e => {
      if (e) {
        res.push(this.columnDefs[e]());
      }
    });

    return res;
  };

  buildNameColumn = () => {
    let model = new NameColumnBuilder()
      .setColumnOptions(NAME_COLUMN_CONFIG({ headerName: "Name" }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .setFilterOptions(NAME_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      // make cell editable
      model.createValueSetter(this.updateName);
    }

    return model.generateColumnOptions();
  };

  buildModuleIdColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: TrainingModuleColDefFieldNamesEnum.ModuleId,
          headerName: I18n.t("grids.moduleId")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(this.canEdit)
      .makeReadOnly(!this.canEdit)
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      // make cell editable
      model.createValueSetter(this.updateModuleId);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildDescriptionColumn = () => {
    let model = new DescriptionColumnBuilder()
      .setColumnOptions(DESCRIPTION_COLUMN_CONFIG())
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(false)
      .makeReadOnly(!this.gridProps.canEdit)
      .setFilterOptions(DESCRIPTION_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      model.setEditableOnDoubleClick(this.updateDescription);
    }

    return model.generateColumnOptions();
  };

  buildTrainingProgressPercentageColumn = () => {
    let model = new NameColumnBuilder({ pinned: undefined })
      .makeEditable(false)
      .makeReadOnly()
      .setColumnOptions({
        field: TrainingModuleColDefFieldNamesEnum.TrainingProgressPercentage,
        headerName: I18n.t("grids.trainingProgressPercentage"),
        valueFormatter: (params: ValueFormatterParams<any>) => {
          const hasValue =
            params.data[TrainingModuleColDefFieldNamesEnum.TrainingProgressPercentage] &&
            params.data[TrainingModuleColDefFieldNamesEnum.TrainingProgressPercentage] > 0;

          return hasValue ? params.data[TrainingModuleColDefFieldNamesEnum.TrainingProgressPercentage] + "%" : "";
        },
        cellEditorParams: {
          min: 0,
          max: 100
        }
      })
      .setFilterOptions(BASE_FILTER_CONFIG);
    return model.generateColumnOptions();
  };

  buildTrainingProgressColumn = () => {
    let model = new AutocompleteColumnBuilder()
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .setColumnOptions({
        field: TrainingModuleColDefFieldNamesEnum.TrainingProgress,
        headerName: I18n.t("grids.trainingProgress"),
        cellEditorParams: {
          field: TrainingModuleColDefFieldNamesEnum.TrainingProgress,
          getValueLabel: (ee: any) => {
            return ee;
          },
          options: this.trainingProgress ?? [],
          valueSelector: "name",
          allowFreeText: true,
          charInputNumber: 1,
          filterFn: (items, query) => {
            const lowerQuery = query.toLowerCase();
            return items.filter((item: FP.Entities.ITrainingProgress) => {
              const name = `${item.name}`.toLowerCase();
              return name.indexOf(lowerQuery) > -1;
            });
          }
        }
      })
      .withCellEditor()
      .setValueSetter(params => {
        if (!params.newValue) return false;
        this.updateTrainingProgress(+params.data.id, params.newValue.id);
        return true;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }
    return model.generateColumnOptions();
  };

  buildStartDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(
        DATE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.StartDate, headerName: I18n.t("grids.startDate") })
      )
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor(CommonColDefFieldNamesEnum.StartDate, "")
      .setValueFormatter(CommonColDefFieldNamesEnum.StartDate)
      .setValueGetterByFieldFn(CommonColDefFieldNamesEnum.StartDate)
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => true)
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.StartDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.StartDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.StartDate];
            }

            let startDate = moment(e);
            let endDate =
              params.data[CommonColDefFieldNamesEnum.EndDate] &&
              params.data[CommonColDefFieldNamesEnum.EndDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.EndDate]);

            if (!(startDate === endDate) && (!endDate || endDate > startDate)) {
              this.updateStartDate(params.data.id, startDate.toDate());
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.StartDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildEndDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(DATE_COLUMN_CONFIG({ field: "endDate", headerName: I18n.t("grids.endDate") }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor("endDate", "")
      .setValueFormatter("endDate")
      .setValueGetterByFieldFn("endDate")
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => true)
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.EndDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.EndDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.EndDate];
            }

            let endDate = moment(e);
            let startDate =
              params.data[CommonColDefFieldNamesEnum.StartDate] &&
              params.data[CommonColDefFieldNamesEnum.StartDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.StartDate]);

            if (
              !(endDate === startDate) &&
              (!startDate || laterDateComparator(endDate.toDate(), startDate.toDate()) === true)
            ) {
              this.updateEndDate(params.data.id, endDate.toDate());
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.EndDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildOwnerColumn = () => {
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions({
        ...AUTOCOMPLETE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.Owner, headerName: I18n.t("grids.owner") }),
        ...OWNER_NAME_COLUMN_CONFIG(CommonColDefFieldNamesEnum.Owner, this.projectTeamMembers)
      })
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...OWNER_NAME_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueGetter(e => {
        if (!e.data[CommonColDefFieldNamesEnum.Owner]) return;
        return `${e.data[CommonColDefFieldNamesEnum.Owner] ?? ""}`;
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.ownerId = params.newValue.id;
        params.data[CommonColDefFieldNamesEnum.Owner] = `${params.newValue.firstName} ${params.newValue.lastName}`;

        this.updateOwnerName(params.data.id, params.newValue.id);
        return true;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildDurationColumn = () => {
    let model = new DurationColumnBuilder()
      .setColumnOptions({
        ...DURATION_COLUMN_CONFIG({
          field: TrainingModuleColDefFieldNamesEnum.Duration,
          headerName: I18n.t("grids.duration")
        })
      })
      .setFilterOptions({
        ...DURATION_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setColumnOptions({
        valueFormatter: e => {
          const totalMinutes = Number(e.data[TrainingModuleColDefFieldNamesEnum.Duration]);
          if (isNaN(totalMinutes) || totalMinutes <= 0) {
            return "0 minutes";
          }
          if (totalMinutes < 60) {
            return `${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`;
          }
          if (totalMinutes < 1440) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            let result = `${hours} hour${hours === 1 ? "" : "s"}`;
            if (minutes > 0) {
              result += ` ${minutes} minute${minutes === 1 ? "" : "s"}`;
            }
            return result;
          }
          const days = Math.floor(totalMinutes / 1440);
          const remainder = totalMinutes % 1440;
          const hours = Math.floor(remainder / 60);
          const minutes = remainder % 60;
          let result = `${days} day${days === 1 ? "" : "s"}`;
          if (hours > 0) {
            result += ` ${hours} hour${hours === 1 ? "" : "s"}`;
          }
          if (minutes > 0) {
            result += ` ${minutes} minute${minutes === 1 ? "" : "s"}`;
          }
          return result;
        }
      })
      .setValueGetter(e => {
        if (!e.data[TrainingModuleColDefFieldNamesEnum.Duration]) return;
        return `${e.data[TrainingModuleColDefFieldNamesEnum.Duration] ?? 0}`;
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;
        let totalMinutes;
        if (typeof params.newValue === "string" && params.newValue.includes(":")) {
          const parts = params.newValue.split(":");
          if (parts.length !== 3) return false;
          const days = parseInt(parts[0], 10) || 0;
          const hours = parseInt(parts[1], 10) || 0;
          const minutes = parseInt(parts[2], 10) || 0;
          totalMinutes = days * 1440 + hours * 60 + minutes;
        } else {
          totalMinutes = Number(params.newValue);
        }
        if (isNaN(totalMinutes)) return false;
        if (params.data[TrainingModuleColDefFieldNamesEnum.Duration] === totalMinutes) return false;
        params.data[TrainingModuleColDefFieldNamesEnum.Duration] = totalMinutes;
        this.updateDuration(params.data.id, totalMinutes);
        return true;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  //#region Update Field Methods

  updateDescription = async (entityId: number, text: string) => {
    await this.updateTextField(TrainingModuleField.description, entityId, text);
    await this.onFieldUpdate();
  };
  updateName = async (entityId: number, text: string) => {
    await this.updateTextField(TrainingModuleField.name, entityId, text);
    await this.onFieldUpdate();
  };
  updateStartDate = async (entityId: number, date: Date) => {
    await this.updateDateField(TrainingModuleField.startDate, entityId, date);
    await this.onFieldUpdate();
  };
  updateEndDate = async (entityId: number, date: Date) => {
    await this.updateDateField(TrainingModuleField.endDate, entityId, date);
    await this.onFieldUpdate();
  };
  updateProgressStatus = async (entityId: number, data: any) => {
    await this.updateIdField(TrainingModuleField.progressStatus, entityId, data);
    await this.onFieldUpdate();
  };
  updateOwnerName = async (entityId: number, data: any) => {
    await this.updateIdField(TrainingModuleField.owner, entityId, data);
    await this.onFieldUpdate();
  };
  updateModuleId = async (entityId: number, text: string) => {
    await this.updateTextField(TrainingModuleField.moduleId, entityId, text);
    await this.onFieldUpdate();
  };
  updateDuration = async (entityId: number, numeric: number) => {
    await this.updateNumericField(TrainingModuleField.duration, entityId, numeric);
    await this.onFieldUpdate();
  };
  updateTrainingProgress = async (entityId: number, data: any) => {
    await this.updateIdField(TrainingModuleField.trainingProgress, entityId, data);
    await this.onFieldUpdate();
  };
  //#endregion
}
